/**
 * The card grid + connection dialog shared by the VMS and Analytics tabs.
 *
 * The "test" is deliberately a local simulation (see connectors.ts): it exercises the
 * layout an operator will use — fill in a host, test, save, watch the card flip to
 * Connected — without a connector service behind it.
 *
 * Colours come from the theme tokens only (brand / brand-light / border / success), and
 * never with a Tailwind opacity modifier: these tokens are bare `var()` values, and
 * Tailwind v3 drops `/40` on those silently, which is how a border disappears entirely.
 */
import { useState } from 'react';
import Modal from '../common/Modal';
import { BTN_PRIMARY, BTN_PRIMARY_SM, BTN_SECONDARY, BTN_SECONDARY_SM } from '../common/ui';
import StatusTag from '../common/StatusTag';
import { ArrowPathIcon, LinkIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { Callout, Field, FieldGroup, inputClass } from './ui';
import {
  blankConnection,
  connectorInitials,
  type Connector,
  type ConnectionState,
} from './connectors';

interface Props {
  connectors: Connector[];
  connections: Record<string, ConnectionState>;
  onChange: (id: string, next: ConnectionState) => void;
  canEdit: boolean;
}

const SYNC_INTERVALS = [
  { value: 'manual', label: 'Manual only' },
  { value: '5m', label: 'Every 5 minutes' },
  { value: '15m', label: 'Every 15 minutes' },
  { value: '1h', label: 'Hourly' },
  { value: '24h', label: 'Daily' },
];

export default function ConnectorGrid({ connectors, connections, onChange, canEdit }: Props) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [draft, setDraft] = useState<ConnectionState | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const open = connectors.find((c) => c.id === openId) || null;

  function startConfigure(c: Connector) {
    setDraft(connections[c.id] ? { ...connections[c.id] } : blankConnection(c));
    setShowPassword(false);
    setOpenId(c.id);
  }

  function close() {
    setOpenId(null);
    setDraft(null);
  }

  /** Simulated reachability check — no request is made. */
  function testConnection() {
    if (!draft || !open) return;
    if (!draft.host.trim()) {
      setDraft({ ...draft, status: 'error', message: 'Enter the server address first.' });
      return;
    }
    setDraft({ ...draft, status: 'testing', message: undefined });
    window.setTimeout(() => {
      setDraft((d) =>
        d
          ? {
              ...d,
              status: 'connected',
              testedAt: new Date().toISOString(),
              message: `Reached ${open.name} at ${d.host}:${d.port} — 24 cameras and 3 groups visible.`,
            }
          : d,
      );
    }, 1100);
  }

  function save() {
    if (!draft || !open) return;
    onChange(open.id, { ...draft, status: draft.status === 'testing' ? 'idle' : draft.status });
    close();
  }

  function disconnect(c: Connector) {
    onChange(c.id, blankConnection(c));
  }

  return (
    <>
      <div data-tour="integrations-connectors" className="grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {connectors.map((c) => {
          const conn = connections[c.id];
          const connected = conn?.status === 'connected';
          return (
            <div
              key={c.id}
              className={`flex flex-col rounded-xl border bg-card p-4 shadow-card transition-colors ${
                connected ? 'border-brand' : 'border-border hover:border-brand'
              }`}
            >
              <div className="flex items-start gap-3">
                <div
                  className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl text-[13px] font-semibold transition-colors ${
                    connected ? 'bg-brand text-white' : 'bg-brand-light text-brand'
                  }`}
                >
                  {connectorInitials(c.name)}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-[13px] font-semibold text-foreground">{c.name}</p>
                  <p className="truncate text-[11px] text-muted-foreground">{c.vendor}</p>
                </div>
                {/* A dot, not a pill — nine cards reading "Not connected" would shout. */}
                <span
                  className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${connected ? 'bg-success' : 'bg-border'}`}
                  title={connected ? 'Connected' : 'Not connected'}
                />
              </div>

              <p className="mt-3 flex-1 text-[12px] leading-relaxed text-muted-foreground">{c.summary}</p>

              <div className="mt-3 flex flex-wrap gap-1.5">
                {c.capabilities.map((cap) => (
                  <span
                    key={cap}
                    className="rounded-full bg-muted px-2 py-0.5 text-[10px] text-muted-foreground"
                  >
                    {cap}
                  </span>
                ))}
              </div>

              <div className="mt-4 flex items-center justify-between gap-2 border-t border-border pt-3">
                <div className="min-w-0">
                  <span className="block truncate font-mono text-[10px] text-muted-foreground">{c.protocol}</span>
                  {connected && conn?.host && (
                    <span className="block truncate font-mono text-[10px] text-brand">
                      {conn.host}:{conn.port}
                    </span>
                  )}
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {connected && canEdit && (
                    <button
                      onClick={() => disconnect(c)}
                      className={BTN_SECONDARY_SM}
                    >
                      Disconnect
                    </button>
                  )}
                  <button
                    onClick={() => startConfigure(c)}
                    className={connected ? BTN_SECONDARY_SM : BTN_PRIMARY_SM}
                  >
                    {connected ? 'Edit' : 'Connect'}
                  </button>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <Modal
        open={!!open && !!draft}
        onClose={close}
        size="lg"
        title={open ? `Connect ${open.name}` : ''}
        subtitle={open ? `${open.protocol} · ${open.vendor}` : undefined}
        footer={
          /* Test sits apart from Cancel/Save: it is the step you repeat, not the one
             that ends the dialog. */
          <div className="flex flex-wrap items-center justify-between gap-3">
            <button
              onClick={testConnection}
              disabled={!canEdit || draft?.status === 'testing'}
              className={BTN_SECONDARY}
            >
              {draft?.status === 'testing' ? (
                <><ArrowPathIcon className="h-4 w-4 animate-spin" /> Testing…</>
              ) : (
                <><LinkIcon className="h-4 w-4" /> Test connection</>
              )}
            </button>
            <div className="flex items-center gap-2">
              <button
                onClick={close}
                className={BTN_SECONDARY}
              >
                Cancel
              </button>
              <button
                onClick={save}
                disabled={!canEdit}
                className={BTN_PRIMARY}
              >
                Save connection
              </button>
            </div>
          </div>
        }
      >
        {draft && open && (
          /* The Modal shell is padding-free by design — content owns its inset. */
          <div className="space-y-4 px-5 py-4">
            <p className="text-[12px] leading-relaxed text-muted-foreground">{open.summary}</p>

            <FieldGroup title="Connection">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <Field label="Server address" required className="sm:col-span-2">
                  <input
                    value={draft.host}
                    onChange={(e) => setDraft({ ...draft, host: e.target.value })}
                    placeholder="vms.customer.local"
                    className={inputClass}
                  />
                </Field>
                <Field label="Port">
                  <input
                    value={draft.port}
                    onChange={(e) => setDraft({ ...draft, port: e.target.value })}
                    inputMode="numeric"
                    className={inputClass}
                  />
                </Field>
              </div>
              <label className="flex items-center gap-2 text-[13px] text-foreground">
                <input
                  type="checkbox"
                  checked={draft.verifyTls}
                  onChange={(e) => setDraft({ ...draft, verifyTls: e.target.checked })}
                  className="h-4 w-4 rounded border-border text-brand focus:ring-brand"
                />
                Verify TLS certificate
                <span className="text-[11px] text-muted-foreground">
                  — turn off only for a self-signed appliance certificate
                </span>
              </label>
            </FieldGroup>

            <FieldGroup title="Credentials">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <Field label="Username / client ID">
                  <input
                    value={draft.username}
                    onChange={(e) => setDraft({ ...draft, username: e.target.value })}
                    autoComplete="off"
                    className={inputClass}
                  />
                </Field>
                <Field label="Password / API secret">
                  <div className="relative">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      value={draft.password}
                      onChange={(e) => setDraft({ ...draft, password: e.target.value })}
                      autoComplete="new-password"
                      className={`${inputClass} pr-9`}
                    />
                    <button
                      type="button"
                      onClick={() => setShowPassword((v) => !v)}
                      className="absolute inset-y-0 right-0 flex items-center px-2.5 text-muted-foreground hover:text-foreground"
                      aria-label={showPassword ? 'Hide secret' : 'Show secret'}
                    >
                      {showPassword ? <EyeSlashIcon className="h-4 w-4" /> : <EyeIcon className="h-4 w-4" />}
                    </button>
                  </div>
                </Field>
              </div>
              <p className="text-[11px] text-muted-foreground">
                A read-only account is enough — Trinetra never writes back to the source system.
              </p>
            </FieldGroup>

            <FieldGroup title="Sync">
              <Field
                label="Re-read inventory"
                hint="How often Trinetra checks the source for cameras added, renamed or removed."
              >
                <select
                  value={draft.syncInterval}
                  onChange={(e) => setDraft({ ...draft, syncInterval: e.target.value })}
                  className={inputClass}
                >
                  {SYNC_INTERVALS.map((s) => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </select>
              </Field>
              <div>
                <p className="mb-2 text-[12px] font-medium text-muted-foreground">What this connector brings in</p>
                <div className="flex flex-wrap gap-1.5">
                  {open.capabilities.map((cap) => (
                    <StatusTag key={cap} tone="info">{cap}</StatusTag>
                  ))}
                </div>
              </div>
            </FieldGroup>

            {draft.message && (
              <Callout tone={draft.status === 'error' ? 'danger' : 'success'}>{draft.message}</Callout>
            )}
          </div>
        )}
      </Modal>
    </>
  );
}
